import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'app/store'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import Info from 'core/components/validatedForm/Info'
import MultiDropdownField from 'core/components/validatedForm/MultiDropdownField'
import Grid from 'core/elements/grid'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { getCurrentUser } from 'openstack/helpers'
import { getPortsForVm, listNetworks, updatePort } from '../networks/actions'
import { SharedWithTenants, SubnetsCellComponent } from '../networks/NetworksListPage'
import { listSecurityGroups } from '../networks/security-groups/actions'
import { securityGroupsSelector } from '../networks/security-groups/selectors'
import { networksSelector } from '../networks/selectors'
import { refreshVirtualMachine } from './actions'
import { formatTimestampToLocal, hasOtherUserVm } from './helpers'

export default function EditVmSecurityGroupsModal({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const [loaded, setLoaded] = useState(false)
  const [vmPorts, setVmPorts] = useState([])
  const [loadingVmPorts, setLoadingVmPorts] = useState(true)
  const defaultParams = {
    securityGroups: [],
    currentSecurityGroups: [],
    currentInterfaces: [],
  }

  const { params, getParamsUpdater, updateParams, setParams } = useParams<{
    securityGroups?: any[]
    currentInterfaces?: any
    currentSecurityGroups?: string[]
  }>(defaultParams)

  const { loading: loadingSecurityGroups } = useListAction(listSecurityGroups, {
    params: {},
  })
  // Networks
  useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)
  const securityGroupsList = useSelectorWithParams(securityGroupsSelector, {} as any)

  const addresses = useMemo(() => {
    if (!vm?.addresses) {
      return []
    }
    const networkNames = Object.keys(vm?.addresses || {})
    const interfaces = networkNames.reduce((accum, name) => {
      const networkAddresses = vm.addresses[name]
      // Matching network object for this network name
      const matchingNetwork = allNetworks?.find((net) => net.name === name) || {}
      const withNetworkName = networkAddresses.map((address, idx) => {
        return {
          ...address,
          ...matchingNetwork,
          networkName: name,
          index: idx,
        }
      })
      return [...accum, ...withNetworkName]
    }, [])
    return interfaces.filter((iface) => iface['OS-EXT-IPS:type'] === 'fixed')
  }, [vm])

  useEffect(() => {
    if (!vm?.id) {
      return
    }
    const getPorts = async () => {
      try {
        const response = await getPortsForVm({ vmId: vm.id })
        setVmPorts(response)
      } catch (e) {
        console.log(e)
      } finally {
        setLoadingVmPorts(false)
      }
    }
    getPorts()
  }, [vm?.id])

  useEffect(() => {
    const filteredPortSecurityGroup = vmPorts
      .filter((port) => port.security_groups.length > 0)
      .reduce((acc, port) => {
        return { ...acc, [port.id]: port.security_groups }
      }, {})

    getParamsUpdater('currentInterfaces')(filteredPortSecurityGroup)
  }, [vmPorts])

  const addressTableColumns = [
    {
      key: 'networkName',
      label: 'Network',
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
    },
    { key: 'addr', label: 'IP Address' },
    { key: 'OS-EXT-IPS-MAC:mac_addr', label: 'MAC Address' },
    { key: 'version', label: 'Version' },
    {
      key: 'OS-EXT-IPS:type',
      label: 'Type',
    },
    {
      key: 'index',
      label: 'Security Groups',
      render: (val, item) => {
        // not sure if there could be duplicate mac addresses or not
        const port = vmPorts?.find(
          (vmPort) => item['OS-EXT-IPS-MAC:mac_addr'] === vmPort?.mac_address,
        )
        const securityGroupItems = securityGroupsList?.map((item) => {
          return { label: item?.name, value: item?.id }
        })
        return (
          <MultiDropdownField
            id={`macOfInterface${item?.addr}`}
            value={port?.id ? params.currentInterfaces[port?.id] : []}
            options={securityGroupItems}
            onChange={(value) => {
              updateParams({
                currentInterfaces: { ...params.currentInterfaces, [port?.id]: value },
              })
            }}
          />
        )
      },
    },
    {
      key: 'subnetDetails',
      label: 'Subnets',
      render: (value) => <SubnetsCellComponent value={value} />,
      display: false,
    },
    {
      key: 'port_security_enabled',
      label: 'Port Security',
      render: (val) => (val ? 'Enabled' : 'Disabled'),
      display: false,
    },
    {
      key: 'provider:network_type',
      label: 'Network Type',
      display: false,
    },
    {
      key: 'mtu',
      label: 'MTU',
      display: false,
    },
    {
      key: 'provider:physical_network',
      label: 'Physical Network',
      display: false,
    },
    {
      key: 'router:external',
      label: 'External',
      render: (val) => (val ? 'Yes' : 'No'),
      display: false,
    },
    {
      key: 'provider:segmentation_id',
      label: 'Segmentation ID',
      render: (val) => (val ? val : 'N/A'),
      display: false,
    },
    {
      key: 'admin_state_up',
      label: 'Admin State',
      render: (val) => (val ? 'Up' : 'Down'),
      display: false,
    },
    {
      key: 'dns_domain',
      label: 'DNS Zone',
      render: (val) => (val ? val : '-'),
      display: false,
    },
    {
      key: 'status',
      label: 'Status',
      display: false,
    },
    {
      key: 'availableIps',
      label: 'Available IPs',
      display: false,
    },
    {
      key: 'shared',
      label: 'Shared Tenants',
      render: (value, item) => <SharedWithTenants item={item} value={value} />,
      display: false,
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (time: string) => formatTimestampToLocal(time),
      display: false,
    },
  ]

  const {
    update: updatePorts,
    updating: updatingPorts,
    error: portError,
    reset: resetPort,
  } = useUpdateAction(updatePort)
  const { update: refreshVirtualMachineFn } = useUpdateAction(refreshVirtualMachine)

  useEffect(() => {
    // Prevent resetting of form from background reloads
    if (loaded || loadingSecurityGroups) {
      return
    }
    const initialSecurityGroups = vm?.security_groups?.map((group) => group?.name) || []
    const securityGroups = securityGroupsList.filter((group) =>
      initialSecurityGroups.includes(group?.name),
    )
    updateParams({
      securityGroups,
      currentSecurityGroups: initialSecurityGroups,
    })
    setLoaded(true)
  }, [vm, loadingSecurityGroups, securityGroupsList, loaded])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    resetPort()
    setLoaded(false)
    onClose(success)
  }

  const submitForm = useCallback(async () => {
    const securityGroups = vmPorts.map((port) => {
      let body = {
        port: {
          port_security_enabled: false,
          security_groups: [],
        },
      }
      if (!!params.currentInterfaces[port.id] && params.currentInterfaces[port.id].length > 0) {
        body = {
          port: {
            port_security_enabled: true,
            security_groups: params.currentInterfaces[port.id],
          },
        }
      }
      return updatePorts({ id: port.id, body })
    })

    const results = await Promise.allSettled(securityGroups)
    const allPortsUpdated = results.every((result) => result.status === 'fulfilled')

    // Refresh the VM to get the updated security groups
    await refreshVirtualMachineFn({ id: vm.id })

    if (allPortsUpdated) {
      handleClose(true)
    }
  }, [vm, params, handleClose])

  const { role: userRole, userId } = getCurrentUser()

  return (
    <ModalForm
      open
      title={`Edit Security Groups`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updatingPorts}
      error={portError}
      submitTitle={`Update Security Groups`}
      loading={!loaded}
    >
      {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
        <Info error>NOTE: You are performing an operation on another user's VM.</Info>
      )}
      <FormFieldSection title="Interface Security Groups">
        <Text variant="body2">
          Add or remove interface security groups on VM <b>{vm?.name || vm?.id}</b>
        </Text>
        <Grid
          uniqueIdentifier="addr"
          data={addresses}
          columns={addressTableColumns}
          loading={loadingVmPorts}
          label="Networks"
          showItemsCountInLabel={false}
        />
      </FormFieldSection>
    </ModalForm>
  )
}
